import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { AddIcon, EditIcon } from "@chakra-ui/icons";
import { FaTrash } from "react-icons/fa";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import {
  EDGE_COLUMN_PX,
  tableCellStylesThin,
  tableHeaderStylesThin,
} from "@/components/FinancialShared/tableStyles";
import {
  Contato,
  ContatoTemperaturaEnum,
  IEnderecoCobrancaRetorno,
  IParteContatoCobrancaRetorno,
  ParteContatoCobranca,
} from "@/models/consignado/proposal-billings.model";
import { cepFormatter } from "@/utils/cepFormatter";
import { documentFormatter } from "@/utils/documentFormatter";
import { phoneFormatter } from "@/utils/formatPhone";

// Um lado da seção "Dados de Contato" (tomador ou empregador): tabela de
// identificação (nome + documento, como vêm da cobrança, e endereço editável)
// e a tabela de contatos com adicionar / editar / excluir — as duas com o
// cabeçalho no gradiente, como as outras tabelas do redesign. Só exibe o que
// a rota devolve — nada é derivado aqui.

type Props = {
  parte: ParteContatoCobranca;
  dados: IParteContatoCobrancaRetorno;
  onAdicionar: () => void;
  onEditar: (contato: Contato) => void;
  onExcluir: (contato: Contato) => void;
  onEditarEndereco: () => void;
  excluindoId?: string | null;
};

const TITULO: Record<ParteContatoCobranca, string> = {
  tomador: "Tomador",
  empregador: "Empregador",
};

// "Rua X, 12 - apto 3 · Centro · São Paulo/SP · CEP 01000-000", pulando o que
// vier null. Retorna "" quando não há nada.
export const formatarEndereco = (endereco: IEnderecoCobrancaRetorno | null): string => {
  if (!endereco) return "";
  const logradouro = [endereco.logradouro, endereco.numero].filter(Boolean).join(", ");
  const rua = [logradouro, endereco.complemento].filter(Boolean).join(" - ");
  const cidadeUf = [endereco.cidade, endereco.uf].filter(Boolean).join("/");
  const cep = endereco.cep ? `CEP ${cepFormatter(endereco.cep)}` : "";
  return [rua, endereco.bairro, cidadeUf, cep].filter(Boolean).join(" · ");
};

export default function ParteContatoCard({
  parte,
  dados,
  onAdicionar,
  onEditar,
  onExcluir,
  onEditarEndereco,
  excluindoId,
}: Props) {
  const backgroundStyle = useConditionalBackground();
  const mostraNome = parte === "empregador";
  const endereco = formatarEndereco(dados.endereco);
  const podeAdicionar = !!dados.id;

  return (
    <Box>
      <Text fontSize="md" fontWeight="bold" color="gray.700" mb={2}>
        {TITULO[parte]}
      </Text>

      {/* Identificação da parte no mesmo padrão das outras tabelas (cabeçalho
          no gradiente): nome e documento como vêm da cobrança, endereço
          editável. */}
      <TableContainer borderRadius="lg" overflowX="auto" mb={4}>
        <Table variant="simple">
          <Thead {...backgroundStyle}>
            <Tr>
              <Th {...tableHeaderStylesThin} px={EDGE_COLUMN_PX}>NOME</Th>
              <Th {...tableHeaderStylesThin}>DOCUMENTO</Th>
              <Th {...tableHeaderStylesThin}>ENDEREÇO</Th>
              <Th {...tableHeaderStylesThin} px={EDGE_COLUMN_PX}>AÇÕES</Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr>
              <Td {...tableCellStylesThin} px={EDGE_COLUMN_PX}>
                {dados.nome || "—"}
              </Td>
              <Td {...tableCellStylesThin}>{dados.documento ? documentFormatter(dados.documento) : "—"}</Td>
              <Td
                {...tableCellStylesThin}
                whiteSpace="normal"
                wordBreak="break-word"
                color={endereco ? undefined : "gray.500"}
              >
                {endereco || "Endereço não cadastrado"}
              </Td>
              <Td {...tableCellStylesThin} px={EDGE_COLUMN_PX}>
                <Flex justifyContent="center">
                  <IconButton
                    aria-label={endereco ? "Editar endereço" : "Cadastrar endereço"}
                    icon={<EditIcon />}
                    size="sm"
                    variant="ghost"
                    onClick={onEditarEndereco}
                    isDisabled={!podeAdicionar}
                  />
                </Flex>
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </TableContainer>

      <Flex justifyContent="flex-end" mb={3}>
        <Button
          leftIcon={<AddIcon />}
          colorScheme="secondary"
          size="sm"
          onClick={onAdicionar}
          isDisabled={!podeAdicionar}
        >
          Adicionar novo contato
        </Button>
      </Flex>

      <TableContainer borderRadius="lg" overflowX="auto">
        <Table variant="simple">
          <Thead {...backgroundStyle}>
            <Tr>
              {mostraNome && (
                <Th {...tableHeaderStylesThin} px={EDGE_COLUMN_PX}>
                  NOME
                </Th>
              )}
              <Th {...tableHeaderStylesThin} px={mostraNome ? undefined : EDGE_COLUMN_PX}>
                TELEFONE
              </Th>
              <Th {...tableHeaderStylesThin}>EMAIL</Th>
              <Th {...tableHeaderStylesThin}>CONTATO QUENTE</Th>
              <Th {...tableHeaderStylesThin} px={EDGE_COLUMN_PX}>
                AÇÕES
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {dados.contatos.length === 0 && (
              <Tr>
                <Td
                  {...tableCellStylesThin}
                  colSpan={mostraNome ? 5 : 4}
                  color="gray.500"
                  py={3}
                >
                  Nenhum contato cadastrado.
                </Td>
              </Tr>
            )}
            {dados.contatos.map((contato) => (
              <Tr key={contato.id} _odd={{ bg: "blackAlpha.50" }}>
                {mostraNome && (
                  <Td {...tableCellStylesThin} px={EDGE_COLUMN_PX}>
                    {contato.nome || "—"}
                  </Td>
                )}
                <Td {...tableCellStylesThin} px={mostraNome ? undefined : EDGE_COLUMN_PX}>
                  {phoneFormatter(contato.telefone) || "—"}
                </Td>
                <Td {...tableCellStylesThin}>{contato.email || "—"}</Td>
                <Td {...tableCellStylesThin}>
                  {contato.temperatura ? (
                    <Badge
                      colorScheme={
                        contato.temperatura === ContatoTemperaturaEnum.QUENTE ? "green" : "red"
                      }
                      fontSize="xs"
                    >
                      {contato.temperatura}
                    </Badge>
                  ) : (
                    "—"
                  )}
                </Td>
                <Td {...tableCellStylesThin} px={EDGE_COLUMN_PX}>
                  <Flex justifyContent="center" gap={1}>
                    <IconButton
                      aria-label="Editar contato"
                      icon={<EditIcon />}
                      size="sm"
                      variant="ghost"
                      onClick={() => onEditar(contato)}
                    />
                    <IconButton
                      aria-label="Excluir contato"
                      icon={<FaTrash />}
                      size="sm"
                      variant="ghost"
                      onClick={() => onExcluir(contato)}
                      isLoading={excluindoId === contato.id}
                    />
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
}
